import { inject, Injectable } from '@angular/core';
import { IdbService, StorageConstants, JsonType } from '@techextensor/tab-core-utility';
import { Common } from '@techextensor/tab-core-utility';
import { firstValueFrom } from 'rxjs';
import { Constants, CustomScriptingType } from '../const/constants';
import { HelperFunctionService } from '../services/helper-function.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { TabSdk } from '@techextensor/tab-sdk';
import { CustomComponentRegisterService } from '../services/formio/custom-component-register.service';
import { EntitlementService } from '../services/entitlement.service';

@Injectable({ providedIn: 'root' })
export class DataResolver {
  private customScriptsLoaded = false;
  private readonly _idbService: IdbService = inject(IdbService);
  private readonly _helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
  private readonly _customComponentRegisterService: CustomComponentRegisterService = inject(CustomComponentRegisterService);
  private readonly _entitlementService: EntitlementService = inject(EntitlementService);

  constructor() { }

  async resolve(route: ActivatedRouteSnapshot): Promise<void> {
    const isOrgSetup = route.routeConfig?.children?.[0]?.path?.includes('org-setup');
    if (isOrgSetup) {
      // For org-setup, load data in parallel without waiting
      await this.loadDataParallel();

    } else {
      // For other routes, load data sequentially
      await this.loadDataSequential();

    }

  }

  private async loadDataParallel(): Promise<void> {
    // Load all data in parallel without awaiting
    if (!Common?.tabJson) {
      await this.loadSchema();
    }
  }

  private async loadDataSequential(): Promise<void> {
    // Load data sequentially with awaits
    if (!Common?.tabJson) {
      await this.loadSchema();
    }
    if (!Common?.tabUserPersonalizedData) {
      await this.loadUserPersonalizedData();
    }
    if (!Common?.tabUserPermissions) {
      await this.loadUserPermissions();
    }
    if (!Common?.tabAppObjectAccess) {
      await this.loadAppObjectAccess();
    }
    // CSS + Custom Components
    if (!this.customScriptsLoaded) {
      await this.loadCustomScripts();
    }
  }

  /**
   * Loads the schema data from the database.
   * If the data does not exist in IndexedDB, it fetches the data from the server using the AppHelper service.
   * It then stores the data in IndexedDB and sets it in the Common object.
   * @return {Promise<void>} This function does not return anything.
   */
  async loadSchema() {
    // Fetch the schema from IndexedDB
    let tabData = await this._idbService.getItem(StorageConstants.tabData);

    if (!tabData) {
      // Fetch the schema from the server using the AppHelper service
      const schemaResponse: any = await TabSdk.app.getSchema();
      // New API returns result directly as object (not stringified)
      tabData = schemaResponse?.Result ?? {};
      // Store the schema in IndexedDB
      this._idbService.setItem(StorageConstants.tabData, tabData);
    } else {
      if (tabData?.appId?.toLowerCase() !== TabSdk.store.appId?.toLowerCase()) {
        await this._helperFunctionService.loadApp();
        return;
      }
    }

    // Directly set the schema into the Common object
    Common.tabJson = tabData;
  }

  /**
   * Loads the user personalized data from the database.
   * If the data does not exist in IndexedDB, it fetches the data from the server using the DSQ service.
   * It then stores the data in IndexedDB and sets it in the Common object.
   * @return {Promise<void>} This function does not return anything.
   */
  async loadUserPersonalizedData(): Promise<void> {
    // Fetch the schema from IndexedDB
    let tabUserPersonalizedData = await this._idbService.getItem(StorageConstants.tabUserPersonalizedData);

    if (!tabUserPersonalizedData) {
      // Fetch the user personalized data from the server using the DSQ service
      const userPersonalizationResponse: any = await firstValueFrom(
        this._helperFunctionService.getDSQData(Constants.Get_UserWise_Personalize_Data_Id)
      );
      tabUserPersonalizedData = userPersonalizationResponse?.Result ?? [];
      // Store the data in IndexedDB
      this._idbService.setItem(StorageConstants.tabUserPersonalizedData, tabUserPersonalizedData);
    }

    // Directly set the data into the Common object
    Common.tabUserPersonalizedData = tabUserPersonalizedData;
  }

  /**
   * Loads the user permissions from the database.
   * If the data does not exist in session storage, it fetches the data from the server using the AppHelper service.
   * It then stores the data in session storage and sets it in the Common object.
   * @return {Promise<void>} This function does not return anything.
   */
  async loadUserPermissions(): Promise<void> {
    // Fetch the schema from session storage
    const userPermissions = TabSdk.store.getSession(StorageConstants?.tabUserPermissions);
    if (!userPermissions) {
      const userPermissionsResponse: any = await TabSdk.app.getPermission();
      Common.tabUserPermissions = userPermissionsResponse?.Result ?? [];
      TabSdk.store.setSession(StorageConstants?.tabUserPermissions, Common.tabUserPermissions);
    } else {
      // Parse the stored data from session storage
      Common.tabUserPermissions = userPermissions;
    }

    // Entitlements load here as well as in loadApp. This is the SECOND bootstrap path: permissions come from
    // session storage on a hard refresh, so loadApp is skipped entirely and entitlements would be undefined
    // for the whole session. Unlike permissions they are deliberately NOT cached in session storage - a
    // subscription can lapse mid-session, and a stale "you may write" cached for an hour is exactly the
    // wrong failure.
    //
    // Awaited, so it holds route activation. That is only safe because load() is bounded: it returns what it
    // already holds rather than re-requesting on the normal path, and a request that hangs is abandoned after
    // five seconds. Without both of those this line is the worst kind of dependency - a billing outage that
    // never answers would leave every route pending and the application blank, with nothing logged.
    await this._entitlementService.load();
  }

  /**
   * Loads the application object access from IndexedDB or fetches it from the server.
   * If the data does not exist in IndexedDB, it fetches the data from the server using the AppHelper service,
   * and then stores it in IndexedDB.
   * This function updates the app object access flag when the data is fetched from the server.
   * @return {Promise<void>} This function does not return anything.
   */
  async loadAppObjectAccess(): Promise<void> {
    // Fetch the app object access from IndexedDB
    let tabAppObjectAccess = await this._idbService.getItem(StorageConstants.tabAppObjectAccess);

    if (!tabAppObjectAccess) {
      // Fetch effective permission scopes from the access endpoint
      const appObjectAccessResponse: any = await TabSdk.app.getUserAccess();
      tabAppObjectAccess = appObjectAccessResponse?.Result ?? {};

      // Store the data in IndexedDB
      this._idbService.setItem(StorageConstants.tabAppObjectAccess, tabAppObjectAccess);
    }

    // Directly set the data into the Common object
    Common.tabAppObjectAccess = tabAppObjectAccess;
  }

  /**
   * Loads custom scripts (CSS + Custom Components) from IDB or fetches from server.
   * Unified method that handles both global CSS and dynamic custom components.
   * Pattern: Check flag → IDB → API
   * @return {Promise<void>} This function does not return anything.
   */
  async loadCustomScripts(): Promise<void> {
    // 1. Check flag
    if (this.customScriptsLoaded) {
      return;
    }

    // 2. Try IDB first
    let customScripts = await this._idbService.getItem(Constants.customScripts);

    // 3. If not in IDB, fetch from API
    if (!customScripts) {
      const response: any = await TabSdk.crud.executeDsq({
        dataSourceQueryIdOrName: Constants.Get_AppWise_Global_CustomScript,
        criteria: {
          Reqtokens: {
            AppId: TabSdk.store.appId,
            Type: CustomScriptingType.Design,
            OtherType: CustomScriptingType.CustomComponent
          }
        }
      });

      const results = response?.Result ?? [];

      customScripts = {
        css: results.filter((r: any) => r.Type === CustomScriptingType.Design),
        customComponents: this.mapComponentsByType(
          results.filter((r: any) => r.Type === CustomScriptingType.CustomComponent)
        )
      };

      // Store in IDB
      await this._idbService.setItem(Constants.customScripts, customScripts);
    }

    // 4. Apply CSS (using helper)
    this._helperFunctionService.applyGlobalCss(customScripts.css);

    // 5. Load component metadata
    this._customComponentRegisterService.loadDynamicComponents(customScripts.customComponents);

    // 6. Set flag
    this.customScriptsLoaded = true;
  }

  /**
   * Convert components array to object keyed by component type
   * @param components - Array of component data
   * @returns Object keyed by componentType
   */
  private mapComponentsByType(components: any[]): Record<string, any> {
    const map: Record<string, any> = {};
    for (const comp of components) {
      const config = TabSdk.util.safeJsonParse(comp.Configuration_Json);
      if (config?.componentType) {
        map[config.componentType] = comp;
      }
    }
    return map;
  }
}

// // Fetch the schema from the server using the AppHelper service
// const schemaResponse: any = await firstValueFrom(this._appHelper.getSchema() as any);
// Common.tabJson = JSON.parse(schemaResponse?.Result ?? '{}');
// // Store the schema in IndexedDB
// this._idbService.setItem(StorageConstants.tabData, Common.tabJson);

// const userPersonalizationResponse: any = await firstValueFrom(this._helperFunctionService.getDSQData(Constants.Get_UserWise_Personalize_Data_Id));
// Common.tabUserPersonalizedData = userPersonalizationResponse?.Result ?? [];
// // Store the data in IndexedDB
// this._idbService.setItem(StorageConstants.tabUserPersonalizedData, Common.tabUserPersonalizedData);

// // Fetch the user permissions from the server using the AppHelper service
// const userPermissionsResponse: any = await firstValueFrom(this._appHelper.getPermission() as any);
// Common.tabUserPermissions = userPermissionsResponse?.Result ?? [];
// // Store the data in session storage
// this._sessionStorageService.setSessionStorage(Constants?.tabUserPermissions, Common.tabUserPermissions);
